import {Edge} from 'reactflow';
import uuid from 'uuid-random';

import {makeEdge} from '../../../../libs/shared/core/src/helpers/models/decisionTreeHelpers';
import {
  DecisionTreeEdgeTypesEnum,
  DecisionTreeNodeTypesEnum,
} from '../../../../libs/shared/core/src/helpers/types/decisionTreeTypes';
import {NodeDataFrontChatTypesEnum} from '../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeNodeDataFrontChatModel';
import {
  addSourceHandleEdges,
  computeTreeData,
  nextNodePositionFromDirection,
} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {TreeData} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {flowBuilderDirection} from '../../../../libs/shared/ui/src/components/decisionTrees/flowBuilder/constants';
import {ChatBotBuilderNode, ChatBotBuilderNodeData} from './types';

/*
 * Helpers.
 */

/**
 * Creates a default tree by setting up a default message node and applying the processors
 * to create the edges.
 *
 * This should not be used in production. Instead, use this function to generate a tree state and
 * allow the useReactFlowLayout hook to adjust the nodes and edges. The final adjusted state is
 * what should be stored below, to later initialize our Decision Tree.
 */
export function makeDefaultChatBotBuilderTreeState(): TreeData<ChatBotBuilderNodeData> {
  const startPosition = {x: 0, y: 0};

  // The node following the start node needs to have its x-position halved to match the layout options.
  const startNextNodePosition = nextNodePositionFromDirection(flowBuilderDirection, startPosition);
  const messagePosition = {x: startNextNodePosition.x / 2, y: startNextNodePosition.y};

  const nodes: Array<ChatBotBuilderNode> = [
    {
      id: '0',
      data: {type: NodeDataFrontChatTypesEnum.START},
      position: startPosition,
      type: DecisionTreeNodeTypesEnum.WORKFLOW,
    },
    {
      id: '1',
      data: {
        type: NodeDataFrontChatTypesEnum.MESSAGE,
        message: 'How can we help you today?',
      },
      position: messagePosition,
      type: DecisionTreeNodeTypesEnum.WORKFLOW,
    },
  ];

  const edges: Array<Edge> = [makeEdge(nodes[0].id, nodes[1].id, DecisionTreeEdgeTypesEnum.WORKFLOW)];

  const addedHandleState = addSourceHandleEdges(flowBuilderDirection, nodes, edges, '1', [uuid()], {
    type: NodeDataFrontChatTypesEnum.END,
  });

  return {nodes: addedHandleState.nodes, edges: addedHandleState.edges};
}

/*
 * Pre-processed default tree.
 */

/**
 * Do not modify this manually, except to fix any type issues. This should be created by using the above function,
 * makeDefaultChatBotBuilderTreeState, and then allowing useReactFlowLayout to adjust the node positions.
 * The output is passed through computeTreeData below to ensure no extraneous fields make it through.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const defaultChatBotBuilderTreeState = Object.freeze(
  computeTreeData(
    [
      {
        id: '0',
        type: 'workflow',
        position: {
          x: 0,
          y: 0,
        },
        data: {
          type: 'start',
        },
      },
      {
        id: '1',
        type: 'workflow',
        position: {
          x: 162.5,
          y: 0,
        },
        data: {
          type: 'message',
          message: 'How can we help you today?',
        },
      },
      {
        id: '7b211a4d-48b7-4bb3-9864-d057d8f3329e',
        type: 'placeholder',
        position: {
          x: 487.5,
          y: 0,
        },
        data: {
          type: 'end',
        },
      },
    ],
    [
      {
        id: '0=>1',
        type: 'workflow',
        source: '0',
        target: '1',
        sourceHandle: '0=>1',
      },
      {
        id: '1=>7b211a4d-48b7-4bb3-9864-d057d8f3329e',
        type: 'workflow',
        source: '1',
        target: '7b211a4d-48b7-4bb3-9864-d057d8f3329e',
        sourceHandle: '1=>7b211a4d-48b7-4bb3-9864-d057d8f3329e',
      },
    ],
  ),
) as TreeData<ChatBotBuilderNodeData>;

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const defaultChatBotBuilderSubtreeState = Object.freeze(
  computeTreeData(
    [
      {
        id: '0',
        data: {type: NodeDataFrontChatTypesEnum.START},
        position: {x: 0, y: 0},
        type: DecisionTreeNodeTypesEnum.WORKFLOW,
      },
      {
        id: '1',
        position: {
          x: 162.5,
          y: 0,
        },
        data: {
          type: NodeDataFrontChatTypesEnum.MESSAGE,
          message: 'This is a saved flow. Try adding more steps!',
        },
        type: DecisionTreeNodeTypesEnum.WORKFLOW,
      },
      {
        id: '2',
        type: 'placeholder',
        position: {
          x: 487.5,
          y: 0,
        },
        data: {
          type: 'end',
        },
      },
    ],
    [
      {
        id: '0=>1',
        type: 'workflow',
        source: '0',
        target: '1',
        sourceHandle: '0=>1',
      },
      {
        id: '1=>2',
        type: 'workflow',
        source: '1',
        target: '2',
        sourceHandle: '1=>2',
      },
    ],
  ),
) as TreeData<ChatBotBuilderNodeData>;
